import React, { Fragment, useState, useCallback, type PropsWithChildren } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import AddIcon from '@atlaskit/icon/core/add';
import {
	ExpandButton,
	CreateChildButtonWithExpandToggle,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/expand-button/index.tsx';
import type { MergedCell$key } from '@atlassian/jira-relay/src/__generated__/MergedCell.graphql';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { TriggerPropsType } from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import {
	useIsChildIssueCreateEnabled,
	useIsGridNavigationEnabled,
	useIsTimelineEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { useTimelineCustomComponents } from '../../../../controllers/timeline-custom-components/index.tsx';

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="merged-cell" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

const CELL_PADDING = 8;
const HIERARCHY_LEVEL_PADDING = 12;
const EXPAND_ICON_WIDTH = 24;

export type RenderContent = (args: {
	actionButtons: React.ReactNode;
	isMergedCellFocusedOrHovered?: boolean;
}) => React.ReactNode;

export type Props = {
	issueRef: MergedCell$key;
	itemsConnectionId: string;
	depth: number;
	isRowHoveredOrSelected?: boolean;
	isCreateChildFormVisible?: boolean;
	createChildTriggerProps?: TriggerPropsType;
	groupId?: string;
	renderContent: RenderContent;
	isHierarchyEnabled?: boolean;
	isFocused?: boolean;
};

export const MergedCell = ({
	issueRef,
	itemsConnectionId,
	depth,
	isRowHoveredOrSelected,
	createChildTriggerProps,
	isCreateChildFormVisible,
	groupId,
	renderContent,
	isHierarchyEnabled,
	isFocused,
}: Props) => {
	const issueData = useFragment<MergedCell$key>(
		graphql`
			fragment MergedCell on JiraIssueEdge
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
			) {
				hasChildren(filterByProjectKeys: $projectKeys)
					@include(if: $shouldQueryHasChildren)
					@optIn(to: "JiraSpreadsheetComponent-M2")
				canHaveChildIssues(projectKey: $projectKey)
					@include(if: $shouldQueryHasChildren)
					@optIn(to: "JiraSpreadsheetComponent-M2")
				node {
					id
					issueId
				}
			}
		`,
		issueRef,
	);

	const hasChildren = Boolean(issueData?.hasChildren);
	const canHaveChildIssues = Boolean(issueData?.canHaveChildIssues);
	const issueId = issueData?.node?.issueId;

	const isChildIssueCreateEnabled = useIsChildIssueCreateEnabled();
	const isGridNavigationEnabled = useIsGridNavigationEnabled();
	// State for managing hover
	const [isHovered, setIsHovered] = useState(false);

	const isMergedCellFocusedOrHovered = fg('jira_list_merged_cell_action_buttons_visibility')
		? isFocused || isHovered
		: Boolean(isRowHoveredOrSelected);

	let showCreateChildButton;
	if (fg('jira_list_merged_column_inline_create_icon_style')) {
		showCreateChildButton = isGridNavigationEnabled
			? isChildIssueCreateEnabled && isMergedCellFocusedOrHovered && canHaveChildIssues
			: isChildIssueCreateEnabled && canHaveChildIssues;
	} else {
		showCreateChildButton =
			isChildIssueCreateEnabled && isMergedCellFocusedOrHovered && canHaveChildIssues;
	}

	const createChildButton = showCreateChildButton && issueId && (
		<CreateChildButtonWithExpandToggle
			depth={depth}
			itemId={issueId}
			groupId={groupId}
			itemsConnectionId={itemsConnectionId}
			createChildTriggerProps={createChildTriggerProps}
			isCreateChildFormVisible={isCreateChildFormVisible}
			renderIcon={(iconProps) => <AddIcon {...iconProps} color="var(--ds-icon)" size="small" />}
		/>
	);

	let childCreateButton;
	if (fg('jira_list_merged_column_inline_create_icon_style')) {
		childCreateButton = !isGridNavigationEnabled ? (
			<Box
				testId="native-issue-table.ui.row.issue-row.merged-cell.child-create-button-wrap"
				xcss={actionButtonDisplayVariableStyle}
			>
				{createChildButton}
			</Box>
		) : (
			createChildButton
		);
	} else {
		childCreateButton = createChildButton;
	}

	const timelineCustomComponents = useTimelineCustomComponents();
	const isTimelineEnabled = useIsTimelineEnabled();

	const contextualItemMenu =
		(fg('refactor_issue_table_for_timline') || isTimelineEnabled) &&
		timelineCustomComponents?.ContextualItemMenu &&
		issueData?.node?.id &&
		isMergedCellFocusedOrHovered ? (
			<timelineCustomComponents.ContextualItemMenu id={issueData.node.id} />
		) : null;

	const onMouseLeaveHandler = useCallback(() => {
		setIsHovered(false);
	}, []);

	const onMouseEnterHandler = useCallback(() => {
		setIsHovered(true);
	}, []);

	const actionButtons = (
		<>
			{childCreateButton}
			{contextualItemMenu}
		</>
	);

	let shouldShowExpandButton: boolean | undefined = hasChildren;

	if (isTimelineEnabled || fg('jira_timeline_harmonisation_m1_issue_table_updates')) {
		shouldShowExpandButton = isHierarchyEnabled && shouldShowExpandButton;
	}

	if (shouldShowExpandButton && issueId) {
		return (
			<MaybeUFOSegment>
				<Box
					testId="native-issue-table.ui.row.issue-row.merged-cell"
					xcss={cellStyles}
					tabIndex={isGridNavigationEnabled ? undefined : 0}
					// eslint-disable-next-line jira/react/no-style-attribute
					style={{
						paddingLeft: `${HIERARCHY_LEVEL_PADDING * depth + CELL_PADDING}px`,
					}}
					data-vc="merged-cell"
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...(fg('jira_list_merged_cell_action_buttons_visibility')
						? { onMouseEnter: onMouseEnterHandler, onMouseLeave: onMouseLeaveHandler }
						: {})}
				>
					<Box xcss={expandButtonStyle}>
						<ExpandButton itemId={issueId} itemsConnectionId={itemsConnectionId} depth={depth} />
					</Box>
					{renderContent({
						actionButtons,
						isMergedCellFocusedOrHovered: fg('jira_list_merged_cell_action_buttons_visibility')
							? isMergedCellFocusedOrHovered
							: undefined,
					})}
				</Box>
			</MaybeUFOSegment>
		);
	}

	const cellPadding = isHierarchyEnabled
		? EXPAND_ICON_WIDTH + HIERARCHY_LEVEL_PADDING * depth + CELL_PADDING
		: 0;

	return (
		<MaybeUFOSegment>
			<Box
				testId="native-issue-table.ui.row.issue-row.merged-cell"
				xcss={cellStyles}
				tabIndex={isGridNavigationEnabled ? undefined : 0}
				// eslint-disable-next-line jira/react/no-style-attribute
				style={{
					paddingLeft: `${cellPadding}px`,
				}}
				data-vc="merged-cell"
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...(fg('jira_list_merged_cell_action_buttons_visibility')
					? { onMouseEnter: onMouseEnterHandler, onMouseLeave: onMouseLeaveHandler }
					: {})}
			>
				{renderContent({ actionButtons, isMergedCellFocusedOrHovered })}
			</Box>
		</MaybeUFOSegment>
	);
};

const cellStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	height: ' 100%',
	position: 'relative',

	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});

const expandButtonStyle = xcss({
	display: 'flex',
	alignItems: 'center',
});
const actionButtonDisplayVariableStyle = xcss({
	display: 'var(--action-button-display, "flex")',
});
